import React from 'react';
import { Button, ButtonGroup, Icon } from 'ui';
import { motion, AnimatePresence } from 'framer-motion';
import { type IItems } from 'api/resources/overviews';

interface IItemActionHeaderProps {
	items: IItems[];
	filter: 'all' | 'approved' | 'declined';
	setFilter: (filter: 'all' | 'approved' | 'declined') => void;
	isFilterDropdownOpen: boolean;
	setIsFilterDropdownOpen: (isOpen: boolean) => void;
	handleBulkAction: (isOpen: boolean) => void;
	bulkAction: boolean;
}

export const ItemActionHeader: React.FC<IItemActionHeaderProps> = ({
	items,
	filter,
	setFilter,
	isFilterDropdownOpen,
	setIsFilterDropdownOpen,
	handleBulkAction,
	bulkAction,
}) => {
	const filterDropdownVariants = {
		hidden: { opacity: 0, y: -10 },
		visible: { opacity: 1, y: 0, transition: { duration: 0.2 } },
	};

	const approvedCount = items.filter(
		(item) => item.status === 'approved',
	).length;
	const declinedCount = items.filter(
		(item) => item.status === 'declined',
	).length;

	return (
		<>
			<div className="hidden lg:block">
				<ButtonGroup
					toggle
					activeId={filter}
					onActiveChange={(activeId) => {
						if (activeId) {
							setFilter(activeId as 'all' | 'approved' | 'declined');
						}
					}}
					aria-label="Filter items"
				>
					<Button
						id="all"
						variant={filter === 'all' ? 'primary' : 'secondary'}
						className="rounded-l-lg rounded-r-none"
					>
						All ({items.length})
					</Button>
					<Button
						id="approved"
						variant={filter === 'approved' ? 'primary' : 'secondary'}
						className="rounded-none"
					>
						Approved ({approvedCount})
					</Button>
					<Button
						id="declined"
						variant={filter === 'declined' ? 'primary' : 'secondary'}
						className="rounded-r-lg rounded-l-none"
					>
						Declined ({declinedCount})
					</Button>
				</ButtonGroup>
			</div>
			<div className="flex items-center space-x-2">
				<Button
					variant="ghost"
					size="sm"
					onClick={() => handleBulkAction(!bulkAction)}
					aria-label="Open bulk actions"
				>
					<Icon name="funnel" size="sm" className="fill-primary-badge" />
				</Button>
				<div className="relative">
					<div className="lg:hidden">
						<Button
							variant="ghost"
							size="sm"
							onClick={() => setIsFilterDropdownOpen(!isFilterDropdownOpen)}
							aria-label="Open filter options"
						>
							<Icon
								name="adjustments-horizontal"
								size="sm"
								className="fill-primary-badge"
							/>
						</Button>
					</div>
					<AnimatePresence>
						{isFilterDropdownOpen && (
							<motion.div
								initial="hidden"
								animate="visible"
								exit="hidden"
								variants={filterDropdownVariants}
								className="absolute right-0 border border-gray-300 mt-2 w-56 bg-white rounded-md shadow-lg z-10"
							>
								<div className="">
									<Button
										variant={filter === 'all' ? 'primary' : 'ghost'}
										className="w-full rounded-none rounded-t-md text-left py-2"
										onClick={() => {
											setFilter('all');
											setIsFilterDropdownOpen(false);
										}}
									>
										All ({items.length})
									</Button>
									<hr className="border-t border-gray-300" />
									<Button
										variant={filter === 'approved' ? 'primary' : 'ghost'}
										className="w-full rounded-none text-left px-4 py-2"
										onClick={() => {
											setFilter('approved');
											setIsFilterDropdownOpen(false);
										}}
									>
										Approved ({approvedCount})
									</Button>
									<hr className="border-t border-gray-300" />
									<Button
										variant={filter === 'declined' ? 'primary' : 'ghost'}
										className="w-full rounded-none rounded-b-md text-left px-4 py-2"
										onClick={() => {
											setFilter('declined');
											setIsFilterDropdownOpen(false);
										}}
									>
										Declined ({declinedCount})
									</Button>
								</div>
							</motion.div>
						)}
					</AnimatePresence>
				</div>
			</div>
		</>
	);
};
